import type { Locale } from "./homepage-seo.ts";
import {
  compareResearchRuns,
  type ComparableResearchRun,
  type ResearchRunComparison,
} from "./research-comparison.ts";
import { researchCSV, researchJSONExport } from "./research-export.ts";
import type {
  ResearchRunLink,
  ResearchWorkspace,
} from "./research-workspace.ts";
import type { BacktestExecutionAssumptions, BacktestResult, HistoryItem } from "./types.ts";

export const RESEARCH_COMPARISON_LIMIT = 4;

export interface ResearchRunViewModel {
  link: ResearchRunLink;
  history?: HistoryItem;
  comparable: ComparableResearchRun;
}

export interface ResearchComparisonViewModel {
  comparison: ResearchRunComparison;
  baseline: ResearchRunViewModel;
  runs: ResearchRunViewModel[];
}

function canonicalJSON(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJSON).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJSON(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

function executionContract(
  execution: BacktestExecutionAssumptions | null | undefined,
): string | undefined {
  if (!execution || !Object.keys(execution).length) return undefined;
  return canonicalJSON(execution);
}

function average(values: readonly number[]): number | undefined {
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** Cloud history retains setup-only placeholders with zero metrics and no run evidence. */
export function hasMaterializedResearchResult(
  history: HistoryItem | undefined,
): history is HistoryItem & { result: BacktestResult } {
  const result = history?.result;
  return Boolean(result && (
    (typeof result.run_id === "string" && result.run_id.trim())
    || [result.candles, result.trades, result.equity_curve].some((items) => Array.isArray(items) && items.length > 0)
  ));
}

/**
 * Builds an honest comparison input from a saved result. Missing provenance is
 * deliberately left undefined so legacy runs are labelled limited rather than
 * made to look comparable.
 */
export function researchRunViewModel(
  link: ResearchRunLink,
  history: HistoryItem | undefined,
): ResearchRunViewModel {
  const result = hasMaterializedResearchResult(history) ? history.result : undefined;
  const setup = history?.setup;
  const trades = result?.trades ?? [];
  const remote = link.remoteSummary;
  // Older clients could upload setup placeholders as immutable all-zero
  // summaries. Retain those records, but require evidence before showing zeros.
  const remoteHasEvidence = remote && (
    Object.values(remote.metrics).some((value) => typeof value === "number" && Number.isFinite(value) && value !== 0)
    || [remote.context.dataset_hash, remote.context.provider, remote.context.engine,
      remote.context.execution_contract_hash, remote.context.indicator_contract]
      .some((value) => typeof value === "string" && Boolean(value.trim()))
  );
  const remoteMetrics = remoteHasEvidence ? remote.metrics : undefined;
  const comparable: ComparableResearchRun = {
    id: link.id,
    symbol: result?.symbol ?? setup?.symbol ?? remote?.context.symbol ?? undefined,
    timeframe: result?.timeframe ?? setup?.timeframe ?? remote?.context.timeframe ?? undefined,
    startTime:
      result?.data?.first_candle_time ??
      result?.data?.start_date ??
      setup?.startDate ??
      remote?.context.start_date ?? undefined,
    endTime:
      result?.data?.last_candle_time ??
      result?.data?.end_date ??
      setup?.endDate ??
      remote?.context.end_date ?? undefined,
    datasetSHA256: result?.data?.snapshot_sha256 ?? remote?.context.dataset_hash ?? undefined,
    provider: result?.data?.selected_provider ?? remote?.context.provider ?? undefined,
    executionContract: executionContract(result?.execution) ?? remote?.context.execution_contract_hash ?? undefined,
    strategyFingerprint: link.strategyFingerprint ?? remote?.context.strategy_fingerprint ?? undefined,
    initialCapital: setup?.initialCapital ?? remote?.context.initial_capital ?? undefined,
    tradeDirection: setup?.tradeDirection ?? remote?.context.trade_direction ?? undefined,
    commissionRate: setup?.commissionPercent ?? remote?.context.commission_percent ?? undefined,
    slippageBps: setup?.slippageBps ?? remote?.context.slippage_bps ?? undefined,
    positionSizePct: setup?.positionSizePercent ?? remote?.context.position_size_pct ?? undefined,
    stopLossPct: setup?.stopLossPercent ?? remote?.context.stop_loss_pct ?? undefined,
    takeProfitPct: setup?.takeProfitPercent ?? remote?.context.take_profit_pct ?? undefined,
    exitMode: setup?.exitMode ?? remote?.context.exit_mode ?? undefined,
    metrics: {
      netProfitAmount: result?.performance.net_profit_amount ?? remoteMetrics?.net_profit ?? undefined,
      totalReturnPct: result?.performance.total_profit_percentage ?? remoteMetrics?.total_return_pct ?? undefined,
      maxDrawdownPct: result?.performance.max_drawdown ?? remoteMetrics?.max_drawdown_pct ?? undefined,
      winRatePct: result?.performance.win_rate ?? remoteMetrics?.win_rate ?? undefined,
      profitFactor: result?.confidence?.sample?.profit_factor ?? remoteMetrics?.profit_factor ?? undefined,
      expectancyAmount: result ? average(trades.map((trade) => trade.profit_amount)) : remoteMetrics?.expectancy ?? undefined,
      tradeCount: result ? trades.length : remoteMetrics?.total_trades,
      totalFees: result?.performance.total_fees ?? remoteMetrics?.fees ?? undefined,
    },
  };
  return { link, history, comparable };
}

export function researchRunViewModels(
  links: readonly ResearchRunLink[],
  history: readonly HistoryItem[],
): ResearchRunViewModel[] {
  const byID = new Map(history.map((item) => [item.id, item]));
  return links.map((link) => researchRunViewModel(link, byID.get(link.historyItemID)));
}

export function toggleResearchRunSelection(
  selectedIDs: readonly string[],
  runLinkID: string,
  limit = RESEARCH_COMPARISON_LIMIT,
): { selectedIDs: string[]; limitReached: boolean } {
  if (selectedIDs.includes(runLinkID)) {
    return {
      selectedIDs: selectedIDs.filter((id) => id !== runLinkID),
      limitReached: false,
    };
  }
  if (selectedIDs.length >= limit) {
    return { selectedIDs: [...selectedIDs], limitReached: true };
  }
  return { selectedIDs: [...selectedIDs, runLinkID], limitReached: false };
}

export function buildResearchComparison(
  runs: readonly ResearchRunViewModel[],
  selectedIDs: readonly string[],
  preferredBaselineID?: string,
): ResearchComparisonViewModel | undefined {
  const selectedSet = new Set(selectedIDs);
  const selected = runs.filter((run) => selectedSet.has(run.link.id));
  if (selected.length < 2 || selected.length > RESEARCH_COMPARISON_LIMIT) return undefined;
  const baselineID = preferredBaselineID && selectedSet.has(preferredBaselineID)
    ? preferredBaselineID
    : selected[0].link.id;
  const baseline = selected.find((run) => run.link.id === baselineID);
  if (!baseline) return undefined;
  return {
    comparison: compareResearchRuns(
      selected.map((run) => run.comparable),
      baselineID,
    ),
    baseline,
    runs: [baseline, ...selected.filter((run) => run.link.id !== baselineID)],
  };
}

export interface ResearchWorkspaceExportPayload {
  workspace: Pick<
    ResearchWorkspace,
    | "id"
    | "title"
    | "hypothesis"
    | "objective"
    | "tags"
    | "workflowStatus"
    | "baselineRunLinkID"
    | "revision"
    | "createdAt"
    | "updatedAt"
  > | null;
  comparison: ResearchRunComparison | null;
  runs: Array<{
    link: ResearchRunLink;
    setup: HistoryItem["setup"] | null;
    result: HistoryItem["result"] | null;
  }>;
}

export function researchWorkspaceExportPayload(
  workspace: ResearchWorkspace | undefined,
  runs: readonly ResearchRunViewModel[],
  comparison: ResearchComparisonViewModel | undefined,
): ResearchWorkspaceExportPayload {
  return {
    workspace: workspace
      ? {
          id: workspace.id,
          title: workspace.title,
          hypothesis: workspace.hypothesis,
          objective: workspace.objective,
          tags: workspace.tags,
          workflowStatus: workspace.workflowStatus,
          baselineRunLinkID: workspace.baselineRunLinkID,
          revision: workspace.revision,
          createdAt: workspace.createdAt,
          updatedAt: workspace.updatedAt,
        }
      : null,
    comparison: comparison?.comparison ?? null,
    runs: runs.map((run) => ({
      link: run.link,
      setup: run.history?.setup ?? null,
      result: hasMaterializedResearchResult(run.history) ? run.history.result : null,
    })),
  };
}

export function researchWorkspaceJSON(
  locale: Locale,
  workspace: ResearchWorkspace | undefined,
  runs: readonly ResearchRunViewModel[],
  comparison: ResearchComparisonViewModel | undefined,
  generatedAt?: string,
): string {
  const omitted = new Set(comparison?.comparison.missingFields ?? []);
  const exportedRunIDs = new Set(runs.map((run) => run.link.id));
  for (const link of workspace?.runLinks ?? []) {
    if (!exportedRunIDs.has(link.id)) omitted.add(`run:${link.id}`);
  }
  for (const run of runs) {
    if (!run.history) omitted.add(`history:${run.link.historyItemID}`);
  }
  return researchJSONExport(
    researchWorkspaceExportPayload(workspace, runs, comparison),
    {
      locale,
      scope: workspace ? "workspace" : "comparison",
      omittedFields: [...omitted],
      generatedAt,
    },
  );
}

export function researchWorkspaceCSV(runs: readonly ResearchRunViewModel[]): string {
  const headers = [
    "run_link_id",
    "history_item_id",
    "run_id",
    "created_at",
    "evidence_state",
    "symbol",
    "timeframe",
    "net_profit_amount",
    "total_return_pct",
    "max_drawdown_pct",
    "win_rate_pct",
    "profit_factor",
    "expectancy_amount",
    "trade_count",
    "total_fees",
  ] as const;
  return researchCSV(
    headers,
    runs.map((run) => ({
      run_link_id: run.link.id,
      history_item_id: run.link.historyItemID,
      run_id: run.link.runID,
      created_at: run.link.createdAt,
      evidence_state: run.link.evidenceState,
      symbol: run.comparable.symbol,
      timeframe: run.comparable.timeframe,
      net_profit_amount: run.comparable.metrics.netProfitAmount,
      total_return_pct: run.comparable.metrics.totalReturnPct,
      max_drawdown_pct: run.comparable.metrics.maxDrawdownPct,
      win_rate_pct: run.comparable.metrics.winRatePct,
      profit_factor: run.comparable.metrics.profitFactor,
      expectancy_amount: run.comparable.metrics.expectancyAmount,
      trade_count: run.comparable.metrics.tradeCount,
      total_fees: run.comparable.metrics.totalFees,
    })),
  );
}

export interface ResearchPrintReportLabels {
  title: string;
  generatedAt: string;
  hypothesis: string;
  comparison: string;
  changedFields: string;
  missingFields: string;
  riskNote: string;
  comparisonLevel?: string;
  evidence: Partial<Record<ResearchRunLink["evidenceState"], string>>;
  columns: {
    run: string;
    evidence: string;
    market: string;
    netProfit: string;
    totalReturn: string;
    drawdown: string;
    profitFactor: string;
    expectancy: string;
    trades: string;
    fees: string;
  };
}

function escapeReportHTML(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function printableMetric(
  locale: Locale,
  value: number | undefined,
  style: "number" | "percent" = "number",
): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale, {
    style: style === "percent" ? "percent" : "decimal",
    maximumFractionDigits: 2,
  }).format(style === "percent" ? value / 100 : value);
}

/**
 * Produces a self-contained, print-ready report without remote fonts, scripts,
 * account identifiers, auth state, or raw strategy prompts. The browser can
 * open this local HTML file and use its native Print → Save as PDF workflow.
 */
export function researchWorkspacePrintReport(
  locale: Locale,
  workspace: ResearchWorkspace | undefined,
  runs: readonly ResearchRunViewModel[],
  comparison: ResearchComparisonViewModel | undefined,
  labels: ResearchPrintReportLabels,
  generatedAt = new Date().toISOString(),
): string {
  const direction = locale === "ur" ? "rtl" : "ltr";
  const reportTitle = workspace?.title || labels.title;
  const generatedDate = new Date(generatedAt);
  const generated = Number.isFinite(generatedDate.valueOf())
    ? new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "UTC",
      }).format(generatedDate)
    : generatedAt;
  const rows = runs.map((run, index) => {
    const metrics = run.comparable.metrics;
    const strategy =
      run.history?.result?.localized_strategy_name ||
      run.history?.result?.strategy_name ||
      `${labels.title} ${index + 1}`;
    const market = [run.comparable.symbol, run.comparable.timeframe]
      .filter(Boolean)
      .join(" · ") || "—";
    return `<tr>
      <th scope="row">${escapeReportHTML(strategy)}</th>
      <td>${escapeReportHTML(labels.evidence[run.link.evidenceState] || run.link.evidenceState)}</td>
      <td>${escapeReportHTML(market)}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.netProfitAmount))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.totalReturnPct, "percent"))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.maxDrawdownPct, "percent"))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.profitFactor))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.expectancyAmount))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.tradeCount))}</td>
      <td>${escapeReportHTML(printableMetric(locale, metrics.totalFees))}</td>
    </tr>`;
  }).join("\n");
  const changed = comparison?.comparison.differences.map((item) =>
    `<li><strong>${escapeReportHTML(item.field)}</strong>: ${escapeReportHTML(item.baseline)} → ${escapeReportHTML(item.candidate)}</li>`,
  ).join("") || "";
  const missing = comparison?.comparison.missingFields.map((field) =>
    `<li>${escapeReportHTML(field)}</li>`,
  ).join("") || "";
  const comparisonSummary = comparison
    ? `<section><h2>${escapeReportHTML(labels.comparison)}</h2><p>${escapeReportHTML(labels.comparisonLevel || comparison.comparison.level)}</p>${changed ? `<h3>${escapeReportHTML(labels.changedFields)}</h3><ul>${changed}</ul>` : ""}${missing ? `<h3>${escapeReportHTML(labels.missingFields)}</h3><ul>${missing}</ul>` : ""}</section>`
    : "";
  const hypothesis = workspace?.hypothesis?.trim()
    ? `<section><h2>${escapeReportHTML(labels.hypothesis)}</h2><p>${escapeReportHTML(workspace.hypothesis)}</p></section>`
    : "";
  const columns = labels.columns;

  return `<!doctype html>
<html lang="${escapeReportHTML(locale)}" dir="${direction}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta name="robots" content="noindex,nofollow">
  <title>${escapeReportHTML(reportTitle)}</title>
  <style>
    :root{color-scheme:light;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#12233f;background:#fff}
    body{max-width:1100px;margin:0 auto;padding:32px;line-height:1.5}h1{margin-bottom:4px}h2{margin-top:28px;font-size:1.08rem}
    .meta{color:#4b5d75}.risk{margin-top:28px;padding:14px 16px;border-inline-start:4px solid #e2a800;background:#fff8dc}
    .table-wrap{overflow-x:auto}table{width:100%;border-collapse:collapse;margin-top:18px;font-size:.88rem}th,td{padding:9px;border-bottom:1px solid #d9e1ea;text-align:start;white-space:nowrap}thead th{background:#edf4f7;color:#17364e}
    @media print{body{max-width:none;padding:0}@page{size:A4 landscape;margin:12mm}}
  </style>
</head>
<body>
  <header><h1>${escapeReportHTML(reportTitle)}</h1><p class="meta">${escapeReportHTML(labels.generatedAt.replace("{date}", generated))}</p></header>
  ${hypothesis}
  ${comparisonSummary}
  <section class="table-wrap" aria-label="${escapeReportHTML(labels.title)}">
    <table><thead><tr><th>${escapeReportHTML(columns.run)}</th><th>${escapeReportHTML(columns.evidence)}</th><th>${escapeReportHTML(columns.market)}</th><th>${escapeReportHTML(columns.netProfit)}</th><th>${escapeReportHTML(columns.totalReturn)}</th><th>${escapeReportHTML(columns.drawdown)}</th><th>${escapeReportHTML(columns.profitFactor)}</th><th>${escapeReportHTML(columns.expectancy)}</th><th>${escapeReportHTML(columns.trades)}</th><th>${escapeReportHTML(columns.fees)}</th></tr></thead><tbody>${rows}</tbody></table>
  </section>
  <p class="risk"><strong>${escapeReportHTML(labels.riskNote)}</strong></p>
</body>
</html>`;
}

export function researchExportFilename(
  workspace: ResearchWorkspace | undefined,
  extension: "json" | "csv" | "html",
): string {
  const stem = (workspace?.title || "ungrouped-runs")
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 64) || "research";
  return `${stem}.${extension}`;
}
